import React from 'react';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';

import { CONSTANTS, PEDIGREE } from 'src/components/Constants';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import { config } from '../../pedigree/config';
import { ADOPTED } from '../../pedigree/utils';
import MenuItemRow from '../MenuItemRow';
import { AdoptedIcon } from '../MenuIcons';
import { MENU_TONE } from '../menuTheme';

const nameOf = (m) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ') || `Person ${m.pid}`;

// Dual-parent adoption (Bennett Fig 3): link an adopted-in person to their in-graph BIOLOGICAL
// parents (drawn as a solid connector; the adoptive descent stays dashed). RadioGroups — a
// portalled MUI Select would trip the context-menu ClickAwayListener. Father candidates are
// male members, mother candidates female (a biological parent has a biological sex).
function BiologicalParentsDialog({ node, memberRecords, onSave, onClose }) {
  const candidates = (memberRecords || []).filter((m) => m.pid !== node.pid);
  const fatherCandidates = candidates.filter((m) => m.gender === 'male');
  const motherCandidates = candidates.filter((m) => m.gender === 'female');
  const [fatherId, setFatherId] = React.useState(
    node.bioFatherId != null ? String(node.bioFatherId) : ''
  );
  const [motherId, setMotherId] = React.useState(
    node.bioMotherId != null ? String(node.bioMotherId) : ''
  );

  const parentGroup = (label, value, setValue, options) => (
    <FormControl>
      <FormLabel sx={{ fontSize: '0.85rem' }}>{label}</FormLabel>
      <RadioGroup value={value} onChange={(e) => setValue(e.target.value)}>
        <FormControlLabel
          value=""
          control={<Radio size="small" />}
          label="Not specified"
        />
        {options.map((m) => (
          <FormControlLabel
            key={m.pid}
            value={String(m.pid)}
            control={<Radio size="small" />}
            label={nameOf(m)}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );

  return (
    <CustomDialog
      open={true}
      handleClose={onClose}
      allowBackgroundClose={onClose}
      showActions={true}
      title={'Biological parents'}
      minHeight={240}
      saveBtnLabel={'Save'}
      onSave={() =>
        onSave({
          bioFatherId: fatherId === '' ? null : Number(fatherId),
          bioMotherId: motherId === '' ? null : Number(motherId)
        })
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Link this adopted person to their biological parents already on the
          pedigree. A solid line connects them; the adoptive descent stays
          dashed.
        </Typography>
        {parentGroup(
          'Biological father',
          fatherId,
          setFatherId,
          fatherCandidates
        )}
        <Divider />
        {parentGroup(
          'Biological mother',
          motherId,
          setMotherId,
          motherCandidates
        )}
      </Box>
    </CustomDialog>
  );
}

// Marks a person adopted-in / adopted-out / none. The value lands on the member record and
// persists via the normal pedigree save (config_schema carries `adopted`); no separate
// persistence call is needed. Immutable update (never mutate the memberRecords array in place),
// and the current status is highlighted.
const AdoptedMenu = ({
  node,
  memberRecords,
  setMemberRecords,
  handleClose,
  setPedigreeState
}) => {
  const current = node?.adopted ?? null;
  const [bioOpen, setBioOpen] = React.useState(false);
  const showBioParents =
    config.ENABLE_ADOPTION_DUAL_PARENT && current === ADOPTED.ADOPTED_IN;

  const markSaving = () =>
    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

  const onChange = (value) => {
    setMemberRecords((prev) =>
      prev.map((person) =>
        person.pid === node.pid ? { ...person, adopted: value } : person
      )
    );
    markSaving();
    handleClose();
  };

  const onSaveBioParents = ({ bioFatherId, bioMotherId }) => {
    setMemberRecords((prev) =>
      prev.map((person) =>
        person.pid === node.pid
          ? { ...person, bioFatherId, bioMotherId }
          : person
      )
    );
    markSaving();
    setBioOpen(false);
    handleClose();
  };

  return (
    <>
      <List disablePadding>
        {CONSTANTS.adoptedMap.map((option, idx) => (
          <MenuItemRow
            key={idx}
            // "None" gets an empty gutter rather than a crossed-out bracket:
            // the absence of the notation IS what None means, and the empty
            // slot keeps the three labels aligned.
            icon={option.value ? <AdoptedIcon /> : null}
            tone={MENU_TONE.person}
            label={option.label}
            selected={option.value === current}
            onClick={() => onChange(option.value)}
          />
        ))}
        {showBioParents && (
          <MenuItemRow
            tone={MENU_TONE.person}
            label="Set biological parents…"
            onClick={() => setBioOpen(true)}
          />
        )}
      </List>
      {bioOpen && (
        <BiologicalParentsDialog
          node={node}
          memberRecords={memberRecords}
          onSave={onSaveBioParents}
          onClose={() => setBioOpen(false)}
        />
      )}
    </>
  );
};

export default AdoptedMenu;
